// =============================================================================
// BP-08-05: Vendor Portal Frontend - Zustand Store
// =============================================================================

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ProductStatus, OrderStatus, TimeframePreset } from '@/features/vendor/types/vendorTypes';

// =============================================================================
// Product Filters
// =============================================================================

interface ProductFilters {
  status: ProductStatus | 'all';
  categoryId: string;
  search: string;
  sortBy: 'name' | 'createdAt' | 'price' | 'rating' | 'views';
  sortOrder: 'asc' | 'desc';
}

// =============================================================================
// Order Filters
// =============================================================================

interface OrderFilters {
  status: OrderStatus | 'all';
  dateFrom: string;
  dateTo: string;
  search: string;
  sortBy: 'orderedAt' | 'total' | 'status';
  sortOrder: 'asc' | 'desc';
}

// =============================================================================
// Review Filters
// =============================================================================

interface ReviewFilters {
  rating: number | 'all';
  hasResponse: boolean | undefined;
  productId: string;
  sortBy: 'createdAt' | 'rating' | 'helpful';
  sortOrder: 'asc' | 'desc';
}

// =============================================================================
// UI State
// =============================================================================

interface VendorUIState {
  sidebarCollapsed: boolean;
  currentPage: string;
  analyticsTimeframe: TimeframePreset;
}

// =============================================================================
// Store Interface
// =============================================================================

interface VendorStore {
  // Product filters
  productFilters: ProductFilters;
  setProductFilters: (filters: Partial<ProductFilters>) => void;
  resetProductFilters: () => void;
  
  // Order filters
  orderFilters: OrderFilters;
  setOrderFilters: (filters: Partial<OrderFilters>) => void;
  resetOrderFilters: () => void;
  
  // Review filters
  reviewFilters: ReviewFilters;
  setReviewFilters: (filters: Partial<ReviewFilters>) => void;
  resetReviewFilters: () => void;
  
  // UI state
  ui: VendorUIState;
  setSidebarCollapsed: (collapsed: boolean) => void;
  toggleSidebar: () => void;
  setCurrentPage: (page: string) => void;
  setAnalyticsTimeframe: (timeframe: TimeframePreset) => void;
}

// =============================================================================
// Default Values
// =============================================================================

const defaultProductFilters: ProductFilters = {
  status: 'all',
  categoryId: '',
  search: '',
  sortBy: 'createdAt',
  sortOrder: 'desc',
};

const defaultOrderFilters: OrderFilters = {
  status: 'all',
  dateFrom: '',
  dateTo: '',
  search: '',
  sortBy: 'orderedAt',
  sortOrder: 'desc',
};

const defaultReviewFilters: ReviewFilters = {
  rating: 'all',
  hasResponse: undefined,
  productId: '',
  sortBy: 'createdAt',
  sortOrder: 'desc',
};

const defaultUIState: VendorUIState = {
  sidebarCollapsed: false,
  currentPage: 'dashboard',
  analyticsTimeframe: '30d',
};

// =============================================================================
// Store Implementation
// =============================================================================

export const useVendorStore = create<VendorStore>()(
  persist(
    (set) => ({
      // Product filters
      productFilters: defaultProductFilters,
      setProductFilters: (filters) =>
        set((state) => ({
          productFilters: { ...state.productFilters, ...filters },
        })),
      resetProductFilters: () =>
        set({ productFilters: defaultProductFilters }),

      // Order filters
      orderFilters: defaultOrderFilters,
      setOrderFilters: (filters) =>
        set((state) => ({
          orderFilters: { ...state.orderFilters, ...filters },
        })),
      resetOrderFilters: () =>
        set({ orderFilters: defaultOrderFilters }),

      // Review filters
      reviewFilters: defaultReviewFilters,
      setReviewFilters: (filters) =>
        set((state) => ({
          reviewFilters: { ...state.reviewFilters, ...filters },
        })),
      resetReviewFilters: () =>
        set({ reviewFilters: defaultReviewFilters }),

      // UI state
      ui: defaultUIState,
      setSidebarCollapsed: (collapsed) =>
        set((state) => ({
          ui: { ...state.ui, sidebarCollapsed: collapsed },
        })),
      toggleSidebar: () =>
        set((state) => ({
          ui: { ...state.ui, sidebarCollapsed: !state.ui.sidebarCollapsed },
        })),
      setCurrentPage: (page) =>
        set((state) => ({
          ui: { ...state.ui, currentPage: page },
        })),
      setAnalyticsTimeframe: (timeframe) =>
        set((state) => ({
          ui: { ...state.ui, analyticsTimeframe: timeframe },
        })),
    }),
    {
      name: 'brightpath-vendor-store',
      partialize: (state) => ({
        ui: state.ui,
        productFilters: state.productFilters,
        orderFilters: state.orderFilters,
        reviewFilters: state.reviewFilters,
      }),
    }
  )
);

// =============================================================================
// Selector Hooks
// =============================================================================

export const useProductFilters = () => useVendorStore((state) => state.productFilters);
export const useSetProductFilters = () => useVendorStore((state) => state.setProductFilters);
export const useResetProductFilters = () => useVendorStore((state) => state.resetProductFilters);

export const useOrderFilters = () => useVendorStore((state) => state.orderFilters);
export const useSetOrderFilters = () => useVendorStore((state) => state.setOrderFilters);
export const useResetOrderFilters = () => useVendorStore((state) => state.resetOrderFilters);

export const useReviewFilters = () => useVendorStore((state) => state.reviewFilters);
export const useSetReviewFilters = () => useVendorStore((state) => state.setReviewFilters);
export const useResetReviewFilters = () => useVendorStore((state) => state.resetReviewFilters);

export const useVendorUI = () => useVendorStore((state) => state.ui);
export const useSidebarCollapsed = () => useVendorStore((state) => state.ui.sidebarCollapsed);
export const useToggleSidebar = () => useVendorStore((state) => state.toggleSidebar);
export const useAnalyticsTimeframe = () => useVendorStore((state) => state.ui.analyticsTimeframe);
export const useSetAnalyticsTimeframe = () => useVendorStore((state) => state.setAnalyticsTimeframe);

